"use client";

import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { Shuffle, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/app/components/ui/copy-button";

export default function RandomNumberGeneratorTool() {
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [count, setCount] = useState(1);
  const [generatedNumbers, setGeneratedNumbers] = useState<number[]>([]);

  const generateNumbers = () => {
    if (min >= max) {
      toast.error("Minimum value must be less than maximum value");
      return;
    }

    if (count < 1 || count > 1000) {
      toast.error("Count must be between 1 and 1000");
      return;
    }

    const numbers: number[] = [];
    for (let i = 0; i < count; i++) {
      const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
      numbers.push(randomNum);
    }

    setGeneratedNumbers(numbers);
    toast.success(`${count} random numbers generated!`);
  };

  const copyToClipboard = () => {
    if (generatedNumbers.length === 0) return;
    const text = generatedNumbers.join('\n');
    navigator.clipboard.writeText(text);
    toast.success("Numbers copied to clipboard!");
  };

  const clearAll = () => {
    setGeneratedNumbers([]);
    toast.info("Numbers cleared");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shuffle className="h-5 w-5" />
              Random Number Generator
            </CardTitle>
            <CardDescription>
              Generate random numbers within a specified range
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min">Minimum Value</Label>
                <Input
                  id="min"
                  type="number"
                  value={min}
                  onChange={(e) => setMin(parseInt(e.target.value) || 0)}
                  placeholder="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max">Maximum Value</Label>
                <Input
                  id="max"
                  type="number"
                  value={max}
                  onChange={(e) => setMax(parseInt(e.target.value) || 100)}
                  placeholder="100"
                />
              </div>
            </div>

            <QuantitySlider
              label="Number of Values"
              value={count}
              min={1}
              max={1000}
              step={1}
              onValueChange={setCount}
              hint="1–1000 numbers"
            />

            <div className="flex gap-2">
              <Button onClick={generateNumbers}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Numbers
              </Button>
              <Button onClick={clearAll} variant="outline">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Generated Numbers</CardTitle>
            <CardDescription>
              Your random numbers will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {generatedNumbers.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <div className="grid grid-cols-5 gap-2">
                  {generatedNumbers.map((number, index) => (
                    <div key={index} className="p-2 bg-muted/50 rounded text-center text-sm">
                      {number}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Shuffle className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Click "Generate Numbers" to see random numbers here</p>
              </div>
            )}

            {generatedNumbers.length > 0 && (
              <CopyButton
                text={generatedNumbers.join("\n")}
                variant="default"
                size="default"
                className="w-full"
                successMessage="Numbers copied!"
              >
                Copy All Numbers
              </CopyButton>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Random Number Generator Features</CardTitle>
          <CardDescription>
            Generate random numbers for various purposes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Custom Range</div>
              <div className="text-sm text-muted-foreground">Set any minimum and maximum values</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Bulk Generation</div>
              <div className="text-sm text-muted-foreground">Generate up to 1000 numbers at once</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">True Random</div>
              <div className="text-sm text-muted-foreground">Cryptographically secure random numbers</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
